// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.Izenda.ReportPartGridPivotActions.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

import React from 'react';
import { action } from 'mobx';
import { observer } from 'mobx-react';
import classnames from 'classnames';
import { compose, withPropsOnChange, withProps, withHandlers} from 'recompose';
import ProcessData from 'logic/Izenda.ReportPartGridProcessData';
import ReportPartUtils from 'share/utils/Izenda.ReportPartUtils';
import BrowserUtils from 'share/utils/Izenda.BrowserUtils';
import ColumnDataObject from 'logic/models/Izenda.ColumnDataObject';
import constants from 'logic/data/Izenda.ReportPartGridConstants';
import stylingUtil from './Izenda.ReportPartGridStylingUtil';
const COLLAPSE_STATUS = constants.COLLAPSE_STATUS;

import {
  bind,
  head,
  defaults,
  get,
  getOr,
  isEmpty,
  map,
  isNil,
  mapValues,
  concat,
  flow,
  reduce,
  curry,
  some,
  set,
  chunk,
  every,
  sortBy,
  find,
  last,
  placeholder,
  toString,
  isEqual,
  first,
  zip,
  flatten,
  compact,
  filter,
  forEach
} from 'lodash/fp';

function getCellData(props: any, columnData: any, rowData: any) {
  const { reportPart, forDrillDown, subTotalMapping } = props;
  const rowExpanders = reportPart.reportPartContent.haveRowExpander;
  const element = columnData.element;

  const value = ProcessData.getDataForCell(columnData, rowData, rowExpanders, forDrillDown, subTotalMapping);

  const source = {
    element: ReportPartUtils.buildDataSourceElement(reportPart, element),
    record: element.properties.hasRangePercent ? head(ProcessData.getRawData(columnData, rowData)) : {}
  };

  let conditionalFormattingStyles = {};
  if(!value.isEmpty && !value.dataNotFound){
    const gridStyle = getOr('', 'reportPart.reportPartContent.gridStyle', props);

    let getDefaultCondititonalFormattingValue = true;
    let useExpanders = true;
    if(gridStyle === constants.GRID_STYLES.PIVOT.value){
      useExpanders = getOr(false, 'reportPart.reportPartContent.rows.properties.useExpanders', props);
    }

    if(useExpanders && columnData.type === 'row'){
      const rowDataObject = getOr({}, 'rowDataObject', rowData.rowDataObjects[columnData.uniqueFieldNameAlias]);
      if(rowDataObject.isExpandable && rowDataObject.isExpand){
        conditionalFormattingStyles = ReportPartUtils.getConditionalFormattingForColapsedCell(rowDataObject);
        getDefaultCondititonalFormattingValue = false;
      }
    }

    if(getDefaultCondititonalFormattingValue){
      const columnName = getOr('', 'element.columnName', source);
      const originalRecord = head(ProcessData.getRawData(columnData, rowData)) || {};

      let record = originalRecord;
      let isSideTotalCell = false;
      if(columnData.type == 'side total'){
        record = {};
        record[columnData.key] = originalRecord[columnData.key];
        if(columnData.keyPath)
          forEach(path => {
            const key = path[0];
            record[key] = originalRecord[key];
          }, columnData.keyPath);
        isSideTotalCell = true;
      }   

      const conditionalFormatting = getOr([], 'currentRow.rowDataObject.conditionalFormatting', rowData);
      conditionalFormattingStyles = ReportPartUtils.getCellConditionalFormattingValue(columnName, conditionalFormatting, record, props.reportPart, isSideTotalCell);
    }
  }

  return { value, source, conditionalFormattingStyles };
}

function getCellContentStyle(props, columnData, rowData, columnKey, rowIndex, column, columnIndex, cellData) {
  const { value, source } = cellData;
  const element = columnData.element;
  const cellStyle = element.properties.getUIStyle();
  const reportPartProperties = props.reportPart.getReportPartProperties();
  const columnAlignment = reportPartProperties.columns.alignment;
  const columnWordWrap = reportPartProperties.columns.wordWrap;

  // Only check wordWrap in Report Part Properties if in FieldProperties, wordWrap is null
  cellStyle.wordWrap = isNil(cellStyle.wordWrap) ? columnWordWrap : cellStyle.wordWrap;
  cellStyle.alignment = isNil(cellStyle.alignment) ? columnAlignment : cellStyle.alignment;

  const styleObject = stylingUtil.createStyleObject(cellStyle);

  if (!!value.isEmpty) {
    return styleObject;
  }

  const conditionalFormattingStyles = cellData.conditionalFormattingStyles;
  const fontSize = getOr('', 'fontSize.value', conditionalFormattingStyles);
  styleObject.fontSize = fontSize ? fontSize  + 'px' : styleObject.fontSize;
  styleObject.fontFamily = getOr('', 'fontFamily.value', conditionalFormattingStyles) || styleObject.fontFamily;
  styleObject.fontWeight = getOr('', 'bold.value', conditionalFormattingStyles) ? 'bold' : styleObject.fontWeight ;
  styleObject.textDecoration = getOr('', 'underline.value', conditionalFormattingStyles) ? 'underline' : styleObject.textDecoration;
  styleObject.fontStyle = getOr('', 'italic.value', conditionalFormattingStyles) ? 'italic' : styleObject.fontStyle;
  styleObject.color = getOr('', 'fontColor.value', conditionalFormattingStyles) || styleObject.color;

  const dataCellContent = !isNil(value.orgData) && value.orgData !== 'null' ? value.orgData : value.data;

  if (!rowData.currentRow.isSupTotalRow) {
    styleObject.color = ReportPartUtils.getConfigDataByRange(
      dataCellContent,
      element.properties.dataFormattings.color.textColor,
      source
    ) || styleObject.color;
  }

  return styleObject;
}

function getCellStyle(props, columnData, columnLocInfo, numberOfRow, rowData, rowIndex, columnIndex, cellData) {
  const { value, source } = cellData;
  const reportPart = props.reportPart;
  const reportPartProperties = reportPart.getReportPartProperties();
  const columnVerticalAlignment = reportPartProperties && reportPartProperties.columns.verticalAlignment;
  let styleObject = {};
  const element = columnData.element;

  // Cell border style
  const borderStyle = stylingUtil.getCellStyleSetting(reportPart, columnLocInfo, rowIndex, columnIndex, numberOfRow);
  const currentRow = rowData.currentRow;

  if (
    !!value.isEmpty ||
    (currentRow.isSupTotalRow &&
      (!props.forDrillDown ||
        columnData.type !== ColumnDataObject.COLUMN_TYPE.ROW ||
        currentRow.rowDataObject.isExpand))
  ) {
    return {...borderStyle, ...constants.DEFAULT_PIVOT_GRID_CELL_STYLE };
  }

  const conditionalFormattingStyles = cellData.conditionalFormattingStyles;
  styleObject.backgroundColor = getOr('', 'bgColor.value', conditionalFormattingStyles) || styleObject.backgroundColor;

  const dataCellContent = !isNil(value.orgData) && value.orgData !== 'null' ? value.orgData : value.data;

  if (!currentRow.isSupTotalRow) {
    styleObject = {
      backgroundColor: ReportPartUtils.getConfigDataByRange(
        dataCellContent,
        element.properties.dataFormattings.color.cellColor,
        source
      ) || styleObject.backgroundColor,
      verticalAlign: element.properties.dataFormattings.verticalAlignment || columnVerticalAlignment
    };
  }

  return { ...defaults(borderStyle, styleObject), ...constants.DEFAULT_PIVOT_GRID_CELL_STYLE };
}

function fnGetHeaderCellStyle(props, columnData, columnLocInfo, numberOfRow, rowData, rowIndex, columnIndex) {
  const reportPart = props.reportPart;
  const properties = reportPart.getReportPartProperties();
  const reportPartHeaderProperties = properties.headers;

  const headerStyle = stylingUtil.getCellStyleSetting(
    reportPart,
    columnLocInfo,
    rowIndex,
    columnIndex,
    numberOfRow,
    true
  );

  const elementStyle = {
    backgroundColor: columnData.element.properties.getDefaultsHeaderProperty(
      'font.backgroundColor',
      reportPartHeaderProperties
    ),
    verticalAlign: columnData.element.properties.getDefaultsHeaderProperty(
      'verticalAlignment',
      reportPartHeaderProperties
    )
  };

  const combinedStyle = defaults(headerStyle, elementStyle);
  return { ...combinedStyle, ...constants.DEFAULT_PIVOT_GRID_CELL_STYLE };
}

function fnGetHeaderCellContentStyle(props: any, columnData: any, e: any) {
  const reportPart = props.reportPart;
  const properties = reportPart.getReportPartProperties();
  const reportPartHeaderProperties = properties.headers;
  const cellStyle = columnData.element.properties.getHeaderCellStyle(reportPartHeaderProperties);

  return stylingUtil.createStyleObject(cellStyle);
}

function fnGetHeaderCellContentForPivotHeader(props: any, columnData: any, e: any) {
  const element = columnData.element;
  const reportPart = props.reportPart;

  if (isNil(columnData.element)) {
    if (columnData.isColumnGroup || columnData.canRenderHeader) {
      const properties = reportPart.getReportPartProperties();
      const reportPartHeaderProperties = properties.headers;

      return stylingUtil.createStyleObject({
        fontFamily: get('font.family', reportPartHeaderProperties),
        fontSize: get('font.size', reportPartHeaderProperties),
        bold: get('font.bold', reportPartHeaderProperties),
        italic: get('font.italic', reportPartHeaderProperties),
        underline: get('font.underline', reportPartHeaderProperties),
        textColor: !columnData.canRenderHeader ? get('font.color', reportPartHeaderProperties) : null,
        alignment: get('alignment', reportPartHeaderProperties),
        wordWrap: get('wordWrap', reportPartHeaderProperties)
      });
    }
    return {};
  }
  const records = props.records;
  const { columns } = reportPart.getReportPartProperties();
  const columnAlignment = columns.alignment;
  const columnWordWrap = columns.wordWrap;
  let cellStyle = columnData.element.properties.getUIStyle();
  cellStyle.alignment = isNil(cellStyle.alignment) ? columnAlignment : cellStyle.alignment;
  cellStyle.wordWrap = isNil(cellStyle.wordWrap) ? columnWordWrap : cellStyle.wordWrap;

  const styleObject = stylingUtil.createStyleObject(cellStyle);
  const rawRecord = element.properties.hasRangePercent
    ? head(ProcessData.buildRawRecordFilterForPivotGrid(columnData, records))
    : {};

  const dataSource = {
    element: ReportPartUtils.buildDataSourceElement(reportPart, element),
    record: rawRecord
  };

  styleObject.color = ReportPartUtils.getConfigDataByRange(
    columnData.title,
    element.properties.dataFormattings.color.textColor,
    dataSource
  );

  return styleObject;
}

function isDisplayRowExpander(props, currentRowInfo, columnData) {
  const reportPart = props.reportPart;
  const reportPartContent = reportPart.reportPartContent;
  const rowExpanders = reportPartContent.haveRowExpander;
  return (
    rowExpanders &&
    currentRowInfo &&
    (
      (columnData.type === ColumnDataObject.COLUMN_TYPE.ROW && currentRowInfo.firstRow) ||
      currentRowInfo.mustHaveExpander
    )
  );
}
/**
 * render exapand Icon for row
 * @param  {[type]} currentRowDataObject [Current rowDataObject]
 * @return {[type]}                      [description]
 */
function renderExpandIcon(props, currentRowDataObject) {
  const { reportPart, loader } = props;
  const reportPartContent = reportPart.reportPartContent;
  const rowExpanders = reportPartContent.haveRowExpander;

  if (!currentRowDataObject.isExpandable || !rowExpanders) {
    return null;
  }

  const updateCurrentSideTotalMapping = (newPreviewData) => {
    const existingSideTotalMapping = reportPart.previewData.sideTotalMapping;

    newPreviewData.sideTotalMapping.forEach(mappingData => {
      const existingSideTotalMappingItem = find(existingSideTotalMappingItem => {
        return existingSideTotalMappingItem.fieldNameAlias === mappingData.fieldNameAlias &&
          existingSideTotalMappingItem.groupFieldLevel === mappingData.groupFieldLevel;
      }, existingSideTotalMapping);

      if(!existingSideTotalMappingItem)
        existingSideTotalMapping.push(mappingData);
    });
  };

  const rowExpandIcon = classnames(
    'custom-expand-collapse-icon',
    currentRowDataObject.isExpand ? 'icon-minus-circled' : 'icon-plus-circled'
  );

  const getSubData = function() {
    loader.startLoading();
    if (currentRowDataObject.isExpand) {
      if (!isEmpty(currentRowDataObject.data) || (props.forDrillDown && props.haveSuptotal)) {
        currentRowDataObject.toggleExpand();
        loader.endLoading();
      } else {
        props
          .getDrillDownRecords(reportPart, currentRowDataObject.buildDrillDownParams(false, reportPart.groupTotalValues))
          .then(
            action(function(previewData) {
              const sideTotalColumns = props.sideTotalColumns;

              ProcessData.updateSideTotalMapping(
                sideTotalColumns,
                previewData.sideTotalMapping,
                reportPartContent.columns
              );
              // update data directly to current rowDataObject
              currentRowDataObject.data = map(mapValues(toString), previewData.records);
              currentRowDataObject.subTotalMapping = previewData.subTotalMapping;
              currentRowDataObject.toggleExpand();

              updateCurrentSideTotalMapping(previewData);
            })
          )
          .finally(loader.endLoading.bind(loader));
      }
    } else {
      if (
        (!isEmpty(currentRowDataObject.childs) && get('isExpand', head(currentRowDataObject.childs))) ||
        (!isEmpty(get('data', head(currentRowDataObject.childs))) &&
          get('value', head(currentRowDataObject.childs)) !== 'undefined')
      ) {
            currentRowDataObject.toggleExpand();
            loader.endLoading();
      } else {
        const getChild = function(data, key) {
          for (let i = 0; i < data.length; i++) {
            if (data[i].key === key) {
              return data[i];
            }

            const found = getChild(data[i].childs, key);
            if (found) return found;
          }
        };
        props
          .getDrillDownRecords(reportPart, currentRowDataObject.buildDrillDownParams(true, reportPart.groupTotalValues))
          .then(function(previewData) {
            const rowDataObjects = ProcessData.createRowDataObject(
              previewData.records,
              previewData,
              reportPartContent.rows,
              props.keyPath || [],
              props.forDrillDown
            );
            const newRow = getChild(rowDataObjects, currentRowDataObject.key);
            newRow && (currentRowDataObject.childs = newRow.childs);

            const sideTotalColumns = props.sideTotalColumns;
            ProcessData.updateSideTotalMapping(
              sideTotalColumns,
              previewData.sideTotalMapping,
              reportPartContent.columns
            );

            currentRowDataObject.toggleExpand();
          })
          .finally(loader.endLoading.bind(loader));
      }
    }
  };

  return <span className={rowExpandIcon} onClick={getSubData} />;
}

/**
 *
 * @param {*} currentRecord records information with separator and each row.
 * @param {*} record information of single row.
 */
function checkRecordWithSeparator(currentRecord, record) {
  const values = record.subKeyPath.map(data => data.value);
  if(currentRecord != null && currentRecord.length !=0 && currentRecord[0].separatorInfo) {
    currentRecord.map((data) => {
      if(data.separatorInfo && isEqual(data.separatorInfo.values, values)) {
        data.records = record;
      }
    })
  } else {
    currentRecord = [{
      separatorInfo: null,
      records: record
    }]
  }
  return currentRecord;
}

/**
 *
 * @param {*} currentRecord records information with separator and each row.
 * @param {*} record information of single row.
 */
function checkRecordWithLogicalSeparator(currentRecord, record) {
  const groupMap = [];
  for(let i = 0; i < record.subKeyPath.length; ++i) {
    groupMap.push({
      values: record.subKeyPath[i].map(data => data.value),
      records: record.childs[i]
    });
  }

  if(currentRecord != null && currentRecord.length !=0 && currentRecord[0].separatorInfo) {
    currentRecord.map((data) => {
      if(data.separatorInfo) {
        const group = filter({values: data.separatorInfo.values}, groupMap);
        if(group != null && group.length > 0) {
          data.records = group[0].records;
        }
      }
    })
  } else {
    currentRecord = [{
      separatorInfo: null,
      records: record
    }]
  }
  return currentRecord;
}

/**
 * add 'All' row at the first row for drillDown only
 * @param  {Object} newData
 * @return {Array}
 */
function getFirstRecordForDrilldown(props, newData) {
  const allRow = props.allRow;
  const reportInfo = props.reportPart.reportInfo;
  props.reportPart.reportInfo = props.isLogicalSeparator
    ? checkRecordWithLogicalSeparator(reportInfo, allRow.currentRow.rowDataObject)
    : checkRecordWithSeparator(reportInfo, allRow.currentRow.rowDataObject);
  const allRowObject = get('currentRow.rowDataObject', allRow);

  if (allRowObject.isExpand) {
    allRow.currentRow.isSupTotalRow = false;
    return concat([allRow], newData);
  } else {
    allRow.currentRow.isSupTotalRow = props.haveSupTotal;
    return [allRow];
  }
}

const getColumnGroup = get('element.properties.headerFormating.columnGroup');

function processColumnGroup(columnList, level) {
  const setHeaderValue = function(itemHeader, leaf) {
    const columnHeader = getColumnGroup(leaf);

    itemHeader.value = columnHeader;
    itemHeader.isLeaf = false;
    itemHeader.isHidden = true;
    itemHeader.level = level;
    itemHeader.isInvisible = false;
    itemHeader.isColumnGroup = true;
    itemHeader.hideTopLeftBorder = !columnHeader;
    itemHeader.childs = [leaf];
  };

  const createNewGroup = function(leaf) {
    const header = new ColumnDataObject.ColumnDataObject();

    setHeaderValue(header, leaf);
    return header;
  };

  const addNewGroup = function(acc, leaf) {
    const newGroup = createNewGroup(leaf);

    return {
      leafRowHeaders: concat(acc.leafRowHeaders, newGroup),
      lastGroup: newGroup,
      lastLeaf: leaf
    };
  };

  return flow(
    reduce(
      function(acc, leaf) {
        if (acc.lastLeaf && getColumnGroup(acc.lastLeaf) === getColumnGroup(leaf)) {
          acc.lastGroup.childs = concat(acc.lastGroup.childs, leaf);
          return set('lastLeaf', leaf, acc);
        }
        return addNewGroup(acc, leaf);
      },
      {
        leafRowHeaders: [],
        lastGroup: null,
        lastLeaf: null
      }
    ),
    get('leafRowHeaders')
  )(columnList);
}

function flattenColumnHeader(columnDataObject, includeAllChildren) {
  if (isNil(columnDataObject) || isEmpty(columnDataObject.childs)) {
    return [columnDataObject];
  }
  const flattenChildren = includeAllChildren
    ? columnDataObject.childs.map(col => flattenColumnHeader(col, includeAllChildren))
    : flattenColumnHeader(columnDataObject.childs[0]);
  return flow(
    concat,
    flatten,
    compact
    ) (columnDataObject, flattenChildren);
}

function buildPageColumnHeader(props) {
  const { forDrillDown, rowHeaderColumnDataObject, columnDataObjects, reportPart, embededReportPart } = props;
  let sideTotalColumns = props.sideTotalColumns;
  const reportPartContent = reportPart.reportPartContent;
  const reportPartProperties = reportPartContent.properties;
  const pivotColumnsPerExportedPage = reportPartProperties.view.pivotColumnsPerExportedPage;
  const haveGroupForRow = some(getColumnGroup, rowHeaderColumnDataObject);
  const maxLevel = reportPartContent.columns.visibleElement.length;
  const isMobile = BrowserUtils.isMobileDevice();

  let fullRowHeader = rowHeaderColumnDataObject;
  let onlyColumnGroupRowHeader = fullRowHeader;

  if (haveGroupForRow) {
    onlyColumnGroupRowHeader = fullRowHeader = processColumnGroup(fullRowHeader, maxLevel);
    maxLevel > 1 && (fullRowHeader = [ProcessData.createBlankHeaders(0, maxLevel - 1, fullRowHeader)]);
  } else if (maxLevel) {
    fullRowHeader = [ProcessData.createBlankHeaders(0, maxLevel, fullRowHeader)];
  }

  const buildPage = curry(function(pageColumn) {
    const haveGroupForColumn = forDrillDown && some(getColumnGroup, pageColumn);
    let returnRow;
    let returnColumn = pageColumn;

    if (haveGroupForColumn) {
      returnColumn = processColumnGroup(pageColumn, 1);
    } else if (forDrillDown && haveGroupForRow) {
      returnColumn = [ProcessData.createBlankHeaders(0, 1, pageColumn)];
    }

    const firstVisible = find(get('element'), returnColumn);
    const columns = flattenColumnHeader(firstVisible);
    returnRow = [ProcessData.createBlankHeaders(0, columns.length - 1, rowHeaderColumnDataObject)];
    const rows = flattenColumnHeader(first(returnRow));
    if (rows.length !== columns.length) {
      const emptyRowHeader = [ProcessData.createBlankHeaders(0, 1, rowHeaderColumnDataObject)];
      returnRow = haveGroupForColumn && !haveGroupForRow ? emptyRowHeader : fullRowHeader;
    } else {
      if(haveGroupForRow) {
        returnRow = fullRowHeader;
      }
      const zipArr = zip(columns, rows);
      zipArr.forEach(item => item[0] && (item[0].canRenderHeader = item[1].canRenderHeader = item[0].element.properties.fieldItemVisible));
      returnColumn.forEach(col => {
        const headerCells = flattenColumnHeader(col, true);
        headerCells.forEach(headerCell => headerCell.canRenderHeader = headerCell.element.properties.fieldItemVisible);
      });
    }

    return concat(returnRow, returnColumn);
  });

  let result = {
    pageColumnHeader: [concat(buildPage(columnDataObjects), sideTotalColumns)],
    pageStatus: true
  };
  let sideTotalPage = [];

  if (props.printingMode && pivotColumnsPerExportedPage) {
    if (forDrillDown) {
      result = {
        pageColumnHeader: flow(
          chunk(pivotColumnsPerExportedPage),
          map(buildPage)
        )(columnDataObjects),
        pageStatus: true
      };
    } else if (every({ pivotColumn: pivotColumnsPerExportedPage }, columnDataObjects)) {
      if (!isEmpty(sideTotalColumns)) {
        if (haveGroupForRow) {
          sideTotalColumns = [ProcessData.createBlankHeaders(0, 1, sideTotalColumns)];
        }
        sideTotalPage = [concat(onlyColumnGroupRowHeader, sideTotalColumns)];
      }
      result = {
        pageColumnHeader: flow(
          chunk(1),
          map(buildPage),
          concat(placeholder, sideTotalPage)
        )(columnDataObjects),
        pageStatus: true
      };
    } else {
      result = {
        haveGroupForRow: haveGroupForRow,
        fullRowHeader: fullRowHeader,
        onlyColumnGroupRowHeader: onlyColumnGroupRowHeader,
        pageStatus: false
      };
    }
  }

  result.getStickyValue = function(value) {
    return embededReportPart || isMobile ? false : value;
  };

  return result;
}

/**
 * handling all states of Pivot grid
 * @param  {Object} nextProps
 */
function handleStates(nextProps) {
  const addSideTotal = !!getOr(false, 'reportPartContent.columns.properties.addSideTotal', nextProps.reportPart);
  const {
    reportPartContent,
    previewData,
    previewData: { subTotalMapping }
  } = nextProps.reportPart;
  const reportPartProperties = reportPartContent.properties;
  const pivotColumnsPerExportedPage = reportPartProperties.view.pivotColumnsPerExportedPage;
  const useExpanders = getOr(false, 'rows.properties.useExpanders', reportPartContent);
  const gridStyle = getOr(null, 'properties.generalInfo.gridStyle', reportPartContent);
  const isPivot = gridStyle === constants.GRID_STYLES.PIVOT.value;

  // Get default collapse value:
  let collapseDrillDownByDefault;
  if (reportPartContent.collapseStatus === COLLAPSE_STATUS.EXPAND || nextProps.printingMode) {
    collapseDrillDownByDefault = false;
  } else if (!useExpanders && isPivot) {
    // if "use expanders" in pivoted grid is turned off - do not collapse rows by default.
    collapseDrillDownByDefault = false;
  } else if (reportPartContent.collapseStatus === COLLAPSE_STATUS.COLLAPSE) {
    collapseDrillDownByDefault = true;
  } else {
    collapseDrillDownByDefault = reportPartProperties.view.collapseDrilldownByDefault;
  }

  const rowHeaderColumnDataObject = ProcessData.createRowHeadersDataObject(reportPartContent.rows);

  // Prepare record groups (we have 2 possible inputs here: list of records or list of record groups)
  let recordKeys = [];
  let recordGroups = [];
  let recordGroupCombined = [];
  if (nextProps.records.length) {
    if (!nextProps.records[0].data) {
      // if we're rendering only one group of data:
      recordGroups = [nextProps.records];
      recordGroupCombined = nextProps.records;
      recordKeys = [nextProps.keyPath];
    } else {
      // if we're rendering several groups of data we need to combine all columns
      // to ensure that all possible pivot columns will be found across all groups.
      recordGroups = map(x => x.data, nextProps.records);
      recordGroupCombined = reduce((accum, curr) => {
        return concat(accum, curr.data);
      }, [], nextProps.records);
      recordKeys = map(x => x.key, nextProps.records);
    }
  }

  // Prepare columns data
  const columnDataObjects = ProcessData.createColumnDataObject(
    recordGroupCombined,
    previewData,
    reportPartContent.columns,
    reportPartContent.values,
    reportPartContent.separators,
    nextProps.keyPath
  );

  const forDrillDown = reportPartContent.gridStyle === constants.GRID_STYLES.DRILL_DOWN.value;

  // Prepare rows data
  let rowDataObject = map(r =>
    ProcessData.createRowDataObject(
      r,
      previewData,
      reportPartContent.rows,
      nextProps.keyPath || [],
      forDrillDown,
      !collapseDrillDownByDefault
    ), recordGroups);

  // Prepare side total columns
  let sideTotalColumns = [];
  if (addSideTotal) {
    sideTotalColumns = ProcessData.createSideTotal(previewData, reportPartContent.values);
    ProcessData.updateSideTotalMapping(sideTotalColumns, previewData.sideTotalMapping, reportPartContent.columns);
  }

  // Prepare subtotals
  const isLastSeparatorVisible = flow(
    sortBy('position'),
    last,
    get('isVisible')
  )(reportPartContent.separators.elements);
  let haveSeparator = !isEmpty(nextProps.keyPath);
  const haveSubTotal = some(item => {
    const field = find(['fieldNameAlias', item.fieldNameAlias], reportPartContent.fieldCollection);
    return field && field.isVisible;
  }, subTotalMapping);
  const allGroupSubTotal = haveSubTotal && (!forDrillDown || (haveSeparator && isLastSeparatorVisible));
  const firstRowElement = flow(
    sortBy('position'),
    head
  )(reportPartContent.rows.visibleElement);

  let rowDataObjectSubTotal = [];
  if (allGroupSubTotal) {
    for (let i = 0; i < recordKeys.length; i++) {
      const lastSeparatorElement = flow(
        sortBy('position'),
        last
      )(reportPartContent.separators.visibleElement);
      const lastSeparatorsValue = flow(
        find(function(item) {
          return item.element === lastSeparatorElement;
        }),
        getOr('', 'value')
      )(recordKeys[i]);
      const supTotalRowDataObject = ProcessData.createSupTotalRowDataObject(
        firstRowElement.field.fieldNameAlias,
        rowDataObject[i],
        lastSeparatorElement,
        lastSeparatorsValue,
        previewData
      );
      rowDataObjectSubTotal.push(supTotalRowDataObject);
    }
  }

  // All object.
  let allRow = null;
  if (forDrillDown || useExpanders) {
    const isLogicalSeparator = nextProps.isLogicalSeparator;
    allRow = ProcessData.createAllRowDataObject(
      firstRowElement.field.fieldNameAlias,
      isLogicalSeparator ? rowDataObject : head(rowDataObject),
      isLogicalSeparator ? recordKeys : nextProps.keyPath,
      reportPartContent.rows.elements.length + 1
    );
    allRow.currentRow.isHiddenRowFn = () => isPivot;
  }

  if (!reportPartContent.useSeparator) {
    reportPartContent.setCollapseStatus(COLLAPSE_STATUS.DEFAULT);
  }

  return {
    rowDataObject,
    rowDataObjectSubTotal,
    rowHeaderColumnDataObject,
    columnDataObjects,
    sideTotalColumns,
    subTotalMapping,
    forDrillDown,
    haveSeparator,
    haveSupTotal: haveSubTotal,
    allGroupSuptotal: allGroupSubTotal,
    allRow,
    pivotColumnsPerExportedPage
  };
}

function toggleAllLevels(props, value) {
  const recursiveToggle = action(rows => {
    !isEmpty(rows) &&
      rows.forEach(row => {
        if (!isEmpty(row.childs)) {
          recursiveToggle(row.childs);
          row.isExpand !== value && row.toggleExpand(value);
        }
      });
  });
  recursiveToggle(props.rowDataObject);
}

function collapseAll(props, value) {
  const {
    reportPart,
    loader,
    reportPart: { reportPartContent }
  } = props;
  // Always expand node All
  props.allRow.currentRow.rowDataObject.isExpand = true;

  if (props.haveSupTotal) {
    toggleAllLevels(props, false);
  } else {
    loader.startLoading();
    if (!some(row => isEmpty(row.data), props.rowDataObject)) {
      toggleAllLevels(props, false);
      loader.endLoading();
    } else {
      reportPartContent.setCollapseStatus(-1);
      var minLevel = 0;
      let firstVisibleLevel = Math.min(...reportPartContent.rows.visibleElement.map(e => e.position)) - 1;
      minLevel = Math.max(minLevel, firstVisibleLevel);
      props
        .getDrillDownRecords(reportPart, { expandedLevel: minLevel, filters: [] }, reportPart.previewData.paging.pageIndex)
        .then(previewData => reportPart.setQueryData(previewData))
        .finally(loader.endLoading.bind(loader));
    }
  }
}

function expandAll(props, value) {
  const {
    reportPart,
    loader,
    reportPart: { reportPartContent }
  } = props;
  // Always expand node All
  props.allRow.currentRow.rowDataObject.isExpand = true;

  if (props.haveSupTotal) {
    toggleAllLevels(props, true);
  } else {
    loader.startLoading();
    reportPartContent.setCollapseStatus(1);
    props
      .getDrillDownRecords(reportPart, { expandedLevel: -1, filters: [] }, reportPart.previewData.paging.pageIndex)
      .then(previewData => reportPart.setQueryData(previewData))
      .finally(loader.endLoading.bind(loader));
  }
}

function onToggleColumn(props, header) {
  header.toggleExpand();
}

export default compose(
  withPropsOnChange(['records'], handleStates),
  withHandlers({
    onToggleColumn: curry(onToggleColumn),
    renderExpandIcon: curry(renderExpandIcon),
    getFirstRecordForDrilldown: curry(getFirstRecordForDrilldown),
    isDisplayRowExpander: curry(isDisplayRowExpander),
    fnGetHeaderCellContentStyle: curry(fnGetHeaderCellContentStyle),
    fnGetHeaderCellContentForPivotHeader: curry(fnGetHeaderCellContentForPivotHeader),
    fnGetHeaderCellStyle: curry(fnGetHeaderCellStyle),
    getCellStyle: curry(getCellStyle),
    getCellContentStyle: curry(getCellContentStyle),
    collapseAll: curry(collapseAll),
    expandAll: curry(expandAll),
    getCellData: curry(getCellData)
  }),
  observer,
  withProps(buildPageColumnHeader)
);



// WEBPACK FOOTER //
// ./ui/modules/ReportDesigner/Fields/ReportBody/ReportPartType/ReportPartGrid/Izenda.ReportPartGridPivotActions.js